import { getOrientationLayout } from '../components/map/shapes/SpotShape/helpers';
import { Shape, SpotShape, SpotStateArea } from '../types';
import { getEffectiveSpotAppearance } from './spots';
import { computeWallFloorPolygons } from './wallFloorFill';
import { getAllWallSegments } from './wallPathSegments';

interface Point {
  x: number;
  y: number;
}

/** Absolute polygon area via the shoelace formula. */
const polygonArea = (polygon: Point[]): number => {
  let doubleArea = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    doubleArea += polygon[j].x * polygon[i].y - polygon[i].x * polygon[j].y;
  }
  return Math.abs(doubleArea) / 2;
};

/** Ray-casting point-in-polygon test. */
const isPointInPolygon = (point: Point, polygon: Point[]): boolean => {
  let isInside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const { x: xi, y: yi } = polygon[i];
    const { x: xj, y: yj } = polygon[j];
    const intersects =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersects) isInside = !isInside;
  }
  return isInside;
};

/**
 * Find the wall-enclosed room a spot sits in and return it as a state-area
 * rectangle in the spot's local coordinate space (relative to the spot
 * origin, compensating for spot rotation). Uses the same planar face
 * detection the wall floor fill uses, so "room" means whatever the walls
 * actually enclose. Returns null when the spot isn't inside any closed
 * wall region.
 */
export const detectRoomRectForSpot = (
  shapes: Shape[],
  spot: SpotShape
): NonNullable<SpotStateArea['rect']> | null => {
  const segments = getAllWallSegments(shapes);
  const rooms = computeWallFloorPolygons(segments);
  if (rooms.length === 0) return null;

  // The spot's state dot in world coordinates (the dot, not the origin, is
  // what visually sits inside the room).
  const layout = getOrientationLayout(
    getEffectiveSpotAppearance(spot.spotType, spot.spotAppearance),
    spot.orientation,
    spot.labelPlacement,
    spot.labelOrientation || 'auto',
    0,
    0,
    spot.spacing || 0,
    spot.rowSpacing || 0,
    spot.width,
    spot.height
  );
  const radians = ((spot.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dotWorld: Point = {
    x: spot.x + layout.circle.x * cos - layout.circle.y * sin,
    y: spot.y + layout.circle.x * sin + layout.circle.y * cos
  };

  // Several nested faces can contain the spot (the room AND the whole
  // building outline). The tightest enclosure — the smallest containing
  // polygon — is the room the user means.
  const containingRooms = rooms.filter((candidate) =>
    isPointInPolygon(dotWorld, candidate.points)
  );
  if (containingRooms.length === 0) return null;
  const room = containingRooms.reduce((smallest, candidate) =>
    polygonArea(candidate.points) < polygonArea(smallest.points)
      ? candidate
      : smallest
  );

  // Bounding box of the room in the spot's local (rotated) frame:
  // local = R(-θ) · (world - origin).
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of room.points) {
    const dx = point.x - spot.x;
    const dy = point.y - spot.y;
    const localX = dx * cos + dy * sin;
    const localY = -dx * sin + dy * cos;
    if (localX < minX) minX = localX;
    if (localY < minY) minY = localY;
    if (localX > maxX) maxX = localX;
    if (localY > maxY) maxY = localY;
  }

  return {
    x: Math.round(minX),
    y: Math.round(minY),
    width: Math.round(maxX - minX),
    height: Math.round(maxY - minY),
    cornerRadius: 0
  };
};
